/**
 * HealthReportPublicationReviewPage — Issue #177.
 *
 * Admin queue for reviewed customer-visible health trend / AI report publications.
 *
 * 红线：
 *   - 只通过 useHealthReportPublications hook 与后端交互，不手写 fetch。
 *   - 所有 mutation 调用必须使用后端返回值更新本地 state（不要乐观假状态）。
 *   - 撤回必须强制非空原因。
 *   - 不展示 AI 草稿之外的医生注释；草稿/已审稿/已发布/已撤回的状态对管理员可见，
 *     但 customer-visible 标记只在 published 时为 true。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Send, Undo2 } from 'lucide-react';
import AccessDenied from '@/components/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import {
  HEALTH_REPORT_PUBLICATION_PERMISSIONS,
  hasPermission,
} from '@/lib/healthReportPublicationPermissions';
import {
  useHealthReportPublications,
  type HealthReportPublicationAdminDto,
  type HealthReportPublicationState,
  type HealthReportPublicationKind,
} from '@/hooks/useHealthReportPublications';
import {
  AdminDataTable,
  type AdminDataTableAction,
  type AdminDataTableColumn,
} from '@/components/business/AdminDataTable';
import { Button } from '@/components/ui/button';

const REVIEW_STATES: readonly HealthReportPublicationState[] = [
  'draft',
  'reviewed',
  'published',
  'retracted',
];

const STATE_LABELS: Record<HealthReportPublicationState, string> = {
  draft: '草稿',
  reviewed: '已审核',
  published: '已发布',
  retracted: '已撤回',
};

const STATE_TONE: Record<HealthReportPublicationState, string> = {
  draft: 'bg-slate-100 text-slate-600',
  reviewed: 'bg-amber-100 text-amber-700',
  published: 'bg-blue-100 text-blue-700',
  retracted: 'bg-purple-100 text-purple-700',
};

const KIND_LABELS: Record<HealthReportPublicationKind, string> = {
  health_trend: '健康趋势',
  health_report_summary: '体检报告摘要',
  ai_prognosis_summary: 'AI 评估摘要',
};

const BADGE_BASE = 'inline-flex rounded-full px-2 py-1 text-xs font-medium';

function fmtDateTime(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

interface ReviewModalState {
  publicationId: string;
  expectedVersion: number;
  note: string;
  sourceProposalId: string;
  sourceProposalVersionId: string;
  sourceProposalReviewId: string;
}

interface RetractModalState {
  publicationId: string;
  expectedVersion: number;
  reason: string;
}

export default function HealthReportPublicationReviewPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(
    permissions,
    HEALTH_REPORT_PUBLICATION_PERMISSIONS.READ,
  );
  const canReview = hasPermission(
    permissions,
    HEALTH_REPORT_PUBLICATION_PERMISSIONS.REVIEW,
  );
  const canPublish = hasPermission(
    permissions,
    HEALTH_REPORT_PUBLICATION_PERMISSIONS.PUBLISH,
  );

  const {
    publications,
    loading,
    error,
    fetchPublications,
    reviewPublication,
    publishPublication,
    retractPublication,
  } = useHealthReportPublications();

  // Issue #345 audit fix2: 后端 `/api/v1/health-report-publications` 仅按
  // reviewState 过滤；之前的 `title` 文本字段会在当前加载的数组上做前端
  // 子串过滤，与（未来若分页时）真实命中数和翻页 total 不一致。在后端补
  // 齐 title 模糊查询前，工具栏只保留 state 下拉。
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    state: 'draft',
  });
  const [review, setReview] = useState<ReviewModalState | null>(null);
  const [retract, setRetract] = useState<RetractModalState | null>(null);

  const stateFilter = (searchValues['state'] ?? '') as
    | HealthReportPublicationState
    | '';

  useEffect(() => {
    if (!canRead) return;
    void fetchPublications(
      stateFilter ? { reviewState: stateFilter } : undefined,
    );
  }, [canRead, stateFilter, fetchPublications]);

  const visibleRows = useMemo(() => {
    return [...publications].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [publications]);

  const refresh = useCallback((): void => {
    void fetchPublications(
      stateFilter ? { reviewState: stateFilter } : undefined,
    );
  }, [fetchPublications, stateFilter]);

  const onPublish = useCallback(
    async (row: HealthReportPublicationAdminDto) => {
      if (!canPublish) return;
      const next = await publishPublication(row.id, {
        expectedVersion: row.version,
      });
      if (next) refresh();
    },
    [canPublish, publishPublication, refresh],
  );

  const columns: ReadonlyArray<
    AdminDataTableColumn<HealthReportPublicationAdminDto>
  > = useMemo(
    () => [
      {
        key: 'title',
        header: '标题',
        alwaysVisible: true,
        render: (row) => (
          <div className="space-y-1">
            <div className="font-medium text-slate-800">{row.title}</div>
            <div className="text-xs text-slate-500">{row.summary}</div>
            {row.retractReason ? (
              <div className="text-xs text-purple-600">
                撤回原因：{row.retractReason}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: 'reportKind',
        header: '类型',
        render: (row) => KIND_LABELS[row.reportKind],
      },
      {
        key: 'reviewState',
        header: '状态',
        render: (row) => (
          <span className={cn(BADGE_BASE, STATE_TONE[row.reviewState])}>
            {STATE_LABELS[row.reviewState]}
          </span>
        ),
      },
      {
        key: 'customerId',
        header: '客户',
        render: (row) => (
          <span className="font-mono text-xs">{row.customerId}</span>
        ),
      },
      {
        key: 'sourceProposalReviewId',
        header: '来源审稿',
        defaultHidden: true,
        render: (row) => (
          <span className="font-mono text-xs">
            {row.sourceProposalReviewId ?? '—'}
          </span>
        ),
      },
      {
        key: 'metricsCount',
        header: '指标数',
        defaultHidden: true,
        render: (row) => row.metrics.length,
      },
      {
        key: 'publishedAt',
        header: '发布时间',
        render: (row) => (
          <span className="text-xs text-slate-500">
            {fmtDateTime(row.publishedAt)}
          </span>
        ),
      },
      {
        key: 'version',
        header: '版本',
        defaultHidden: true,
        render: (row) => <span className="text-xs">v{row.version}</span>,
      },
    ],
    [],
  );

  const rowActions: ReadonlyArray<
    AdminDataTableAction<HealthReportPublicationAdminDto>
  > = useMemo(
    () => [
      {
        key: 'mark_reviewed',
        label: '标记审稿通过',
        icon: <CheckCircle2 className="h-3.5 w-3.5" />,
        visible: (row) => canReview && row.reviewState === 'draft',
        onSelect: (row) =>
          setReview({
            publicationId: row.id,
            expectedVersion: row.version,
            note: '',
            sourceProposalId: row.sourceProposalId ?? '',
            sourceProposalVersionId: row.sourceProposalVersionId ?? '',
            sourceProposalReviewId: row.sourceProposalReviewId ?? '',
          }),
      },
      {
        key: 'publish',
        label: '发布给客户',
        icon: <Send className="h-3.5 w-3.5" />,
        visible: (row) =>
          canPublish &&
          (row.reviewState === 'reviewed' || row.reviewState === 'retracted'),
        onSelect: (row) => void onPublish(row),
      },
      {
        key: 'retract',
        label: '撤回（归档）',
        icon: <Undo2 className="h-3.5 w-3.5" />,
        destructive: true,
        visible: (row) => canPublish && row.reviewState === 'published',
        onSelect: (row) =>
          setRetract({
            publicationId: row.id,
            expectedVersion: row.version,
            reason: '',
          }),
      },
    ],
    [canPublish, canReview, onPublish],
  );

  if (!canRead) {
    return <AccessDenied resource="健康报告发布审核" />;
  }

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">健康报告发布审核</h1>
          <p className="text-sm text-slate-500">
            发布到客户端的健康趋势 / AI 报告必须先由审稿人确认来源 Proposal 已审稿通过，
            否则一律不可发布。健康报告属于高敏感数据，仅支持作废 / 归档（撤回），
            不提供物理删除。
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={refresh}>
          刷新
        </Button>
      </header>

      <AdminDataTable<HealthReportPublicationAdminDto>
        testIdPrefix="health-report-publications"
        columns={columns}
        rows={visibleRows}
        isLoading={loading && publications.length === 0}
        error={error}
        onRetry={refresh}
        emptyMessage="暂无记录"
        filters={{
          fields: [
            {
              kind: 'select',
              name: 'state',
              label: '状态',
              options: REVIEW_STATES.map((s) => ({
                value: s,
                label: STATE_LABELS[s],
              })),
            },
          ],
          values: searchValues,
          onSubmit: (next) => setSearchValues(next),
          onReset: () => setSearchValues({ state: '' }),
        }}
        rowActions={rowActions}
      />

      {review ? (
        <ReviewModal
          value={review}
          onChange={(next) => setReview(next)}
          onCancel={() => setReview(null)}
          onConfirm={async () => {
            const sp = review.sourceProposalId.trim();
            const sv = review.sourceProposalVersionId.trim();
            const sr = review.sourceProposalReviewId.trim();
            if (sp.length === 0 || sv.length === 0 || sr.length === 0) return;
            const next = await reviewPublication(review.publicationId, {
              expectedVersion: review.expectedVersion,
              note: review.note.trim() || undefined,
              sourceProposalId: sp,
              sourceProposalVersionId: sv,
              sourceProposalReviewId: sr,
            });
            if (next) {
              setReview(null);
              refresh();
            }
          }}
        />
      ) : null}

      {retract ? (
        <ReasonModal
          title="撤回客户可见健康报告"
          confirmLabel="确认撤回"
          confirmTone="bg-purple-600 hover:bg-purple-700"
          value={retract.reason}
          onChange={(v) => setRetract({ ...retract, reason: v })}
          onCancel={() => setRetract(null)}
          onConfirm={async () => {
            const trimmed = retract.reason.trim();
            if (trimmed.length === 0) return;
            const next = await retractPublication(retract.publicationId, {
              expectedVersion: retract.expectedVersion,
              reason: trimmed,
            });
            if (next) {
              setRetract(null);
              refresh();
            }
          }}
        />
      ) : null}
    </div>
  );
}

interface ReviewModalProps {
  value: ReviewModalState;
  onChange: (next: ReviewModalState) => void;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}

function ReviewModal({ value, onChange, onCancel, onConfirm }: ReviewModalProps) {
  const sourceFilled =
    value.sourceProposalId.trim().length > 0 &&
    value.sourceProposalVersionId.trim().length > 0 &&
    value.sourceProposalReviewId.trim().length > 0;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div className="w-full max-w-md rounded bg-white p-4 shadow-lg">
        <h2 className="text-lg font-semibold">确认审稿通过</h2>
        <p className="mt-1 text-sm text-slate-500">
          标记审稿通过前，请确认来源 Proposal / 审稿记录已通过审核。系统会再次校验。
        </p>
        <label className="mt-3 block text-xs text-slate-600">
          来源 Proposal ID
          <input
            type="text"
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-mono"
            value={value.sourceProposalId}
            onChange={(e) =>
              onChange({ ...value, sourceProposalId: e.target.value })
            }
            maxLength={50}
          />
        </label>
        <label className="mt-2 block text-xs text-slate-600">
          来源 ProposalVersion ID
          <input
            type="text"
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-mono"
            value={value.sourceProposalVersionId}
            onChange={(e) =>
              onChange({ ...value, sourceProposalVersionId: e.target.value })
            }
            maxLength={50}
          />
        </label>
        <label className="mt-2 block text-xs text-slate-600">
          来源 ProposalReview ID
          <input
            type="text"
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm font-mono"
            value={value.sourceProposalReviewId}
            onChange={(e) =>
              onChange({ ...value, sourceProposalReviewId: e.target.value })
            }
            maxLength={50}
          />
        </label>
        <label className="mt-2 block text-xs text-slate-600">
          备注（可选，仅管理员可见）
          <textarea
            className="mt-1 h-20 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            value={value.note}
            onChange={(e) => onChange({ ...value, note: e.target.value })}
            maxLength={2000}
          />
        </label>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 text-sm"
            onClick={onCancel}
          >
            取消
          </button>
          <button
            type="button"
            className={cn(
              'rounded bg-amber-600 px-3 py-1 text-sm text-white hover:bg-amber-700',
              !sourceFilled && 'opacity-50',
            )}
            disabled={!sourceFilled}
            onClick={() => void onConfirm()}
          >
            确认审稿通过
          </button>
        </div>
      </div>
    </div>
  );
}

interface ReasonModalProps {
  title: string;
  confirmLabel: string;
  confirmTone: string;
  value: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}

function ReasonModal({
  title,
  confirmLabel,
  confirmTone,
  value,
  onChange,
  onCancel,
  onConfirm,
}: ReasonModalProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div className="w-full max-w-md rounded bg-white p-4 shadow-lg">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="mt-1 text-sm text-slate-500">
          请填写原因。该原因将写入审计日志；撤回后客户不再可见该内容。
        </p>
        <textarea
          className="mt-3 h-28 w-full rounded border border-slate-300 px-2 py-1 text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={2000}
        />
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 text-sm"
            onClick={onCancel}
          >
            取消
          </button>
          <button
            type="button"
            className={cn(
              'rounded px-3 py-1 text-sm text-white',
              confirmTone,
              value.trim().length === 0 && 'opacity-50',
            )}
            disabled={value.trim().length === 0}
            onClick={() => void onConfirm()}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
